import React, { FC, useEffect, useMemo, useRef } from 'react';
import { throttle } from 'lodash';
import classnames from 'classnames';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { SelectedFilter } from '@src/containers/Source/interfaces/filter.interface';
import { SideBar as DrawerSideBar } from '@src/containers/SideBar';
import { FilterKeys } from '@src/containers/Source/enums/filter.enum';
import { MAX_SALARY, MIN_SALARY } from '@src/containers/Source/constants/salaryRange';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { GroupedTalentDictionaryKey } from '@src/containers/Source/constants/groupedTalentDictionary/enums/groupedTalentDictionaryKey.enum';
import { Side } from '@src/containers/SideBar/enums/side.enum';
import { SidebarFilters } from '../SidebarFilters';
import { ToggleItem } from '../interfaces/toggleItem.interface';
import { PlacesSearch } from './PlacesSearch';
import { Search } from './Search';
import { SortingOptions } from './SortingOptions';
import { OnGroupedTalentsClickParams } from './interfaces/onGroupedTalentsClick.interface';
import { TalentList } from './TalentList';
import { SidebarProps } from './props';
import './styles.scss';

const MAP_GROUPED_TALENT_KEY_TO_METRIC_INTERACTION: Record<GroupedTalentDictionaryKey, MetricAction> = {
  [GroupedTalentDictionaryKey.RECENTLY_JOINED]: MetricAction.VIEW_RECENTLY_JOINED,
  [GroupedTalentDictionaryKey.RECENTLY_ACTIVE]: MetricAction.VIEW_RECENTLY_ACTIVE,
  [GroupedTalentDictionaryKey.RECENTLY_REJECTED]: MetricAction.VIEW_RECENTLY_VIEWED,
  [GroupedTalentDictionaryKey.SAVED_TALENTS]: MetricAction.VIEW_RECENTLY_SAVED,
};

export const Sidebar: FC<SidebarProps> = ({
  setIsPlacesSearchOpen,
  isPlacesSearchOpen,
  isSearchOpen,
  setIsSearchOpen,
  isFiltersOpen,
  setIsFiltersOpen,
  verifiedTalentsListFeed,
  isVerifiedTalentsListFeedInitialFetchLoading,
  isVerifiedTalentsListFeedFetchMoreLoading,
  hasMoreVerifiedTalentsListResults,
  loadMoreVerifiedTalentsList,
  onUserSelect,
  isSubscriptionInactive,
}) => {
  const talentsListRef = useRef<HTMLDivElement | null>(null);

  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  const {
    place,
    isRelocation,
    isRemote,
    searchPatterns = [],
    specialities = [],
    seniorities = [],
    salaryRange = [MIN_SALARY.toString(), MAX_SALARY.toString()],
    groupedTalentsKey,
    isOnSite,
    openingId,
  } = queryParams;

  const filteredSearchPatterns = useMemo(
    () => searchPatterns.filter(({ pattern }) => pattern.length),
    [searchPatterns],
  );

  const parsedSalaryRange = salaryRange.map(salary => parseFloat(salary));

  const toggleItemFromArray = (itemsArray: ToggleItem[], item: ToggleItem): ToggleItem[] => {
    if (itemsArray.map(({ id }) => id).includes(item.id)) {
      return itemsArray.filter(it => it.id !== item.id);
    } else {
      return [...itemsArray, item];
    }
  };

  const onFilterSelect = ({ key, value }: SelectedFilter): void => {
    if (key === FilterKeys.SPECIALITY) {
      updateQueryParams({
        groupedTalentsKey: !queryParams.groupedTalentsKey
          ? GroupedTalentDictionaryKey.RECENTLY_JOINED
          : queryParams.groupedTalentsKey,
        specialities: toggleItemFromArray(specialities, value),
      });
    }

    if (key === FilterKeys.SENIORITY) {
      updateQueryParams({
        seniorities: toggleItemFromArray(seniorities, value),
      });
    }

    if (key === FilterKeys.SALARY_RANGE) {
      updateQueryParams({ salaryRange: value.map(num => num.toString()) });
    }
  };

  const onGroupedTalentsClick = ({ userId, index, groupedTalentDictionaryKey }: OnGroupedTalentsClickParams): void => {
    updateQueryParams({
      groupedTalentsKey: groupedTalentDictionaryKey,
      userId: userId,
    });

    if (userId) {
      onUserSelect(index, userId);
    }

    trackEvent({
      context: MetricContext.SOURCE,
      interaction: groupedTalentDictionaryKey
        ? MAP_GROUPED_TALENT_KEY_TO_METRIC_INTERACTION[groupedTalentDictionaryKey]
        : MetricAction.VIEW_TOP_MATCHES,
      payload: {
        'User Id': userId,
      },
    });
  };

  useEffect(() => {
    const onWindowScroll = throttle(() => {
      if (talentsListRef.current) {
        const { scrollHeight, scrollTop, offsetHeight } = talentsListRef.current;

        const distanceFromBottom = scrollHeight - scrollTop - offsetHeight;

        if (distanceFromBottom <= 200) {
          loadMoreVerifiedTalentsList();
        }
      }
    }, 250);

    if (talentsListRef.current && !isSubscriptionInactive) {
      talentsListRef.current.addEventListener('scroll', onWindowScroll);
    }

    return () => {
      if (talentsListRef.current) {
        talentsListRef.current.removeEventListener('scroll', onWindowScroll);
      }

      onWindowScroll.cancel();
    };
  }, [
    isVerifiedTalentsListFeedInitialFetchLoading,
    isVerifiedTalentsListFeedFetchMoreLoading,
    hasMoreVerifiedTalentsListResults,
    isSubscriptionInactive,
  ]);

  const sortingOptions = [
    ...(specialities.length
      ? []
      : [
          {
            label: 'Top matches',
            value: undefined,
          },
        ]),
    {
      label: 'Recently active',
      value: GroupedTalentDictionaryKey.RECENTLY_ACTIVE,
    },
    {
      label: 'Recently joined',
      value: GroupedTalentDictionaryKey.RECENTLY_JOINED,
    },
  ];

  const isFiltersHidden = openingId || groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS;
  const isSortByHidden = groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS;

  return (
    <div className={classnames('talent-pipeline-sidebar', isSortByHidden && 'talent-pipeline-sidebar--no-padding-top')}>
      {!isFiltersHidden && (
        <div
          className={classnames(
            'talent-pipeline-sidebar__places-search',
            isPlacesSearchOpen && 'talent-pipeline-sidebar__places-search--active',
          )}
        >
          <PlacesSearch
            isOnSite={!!isOnSite}
            isRelocation={!!isRelocation}
            isRemote={!!isRemote}
            onClear={() => updateQueryParams({ place: undefined })}
            onDropdownToggle={setIsPlacesSearchOpen}
            onPlaceSelect={place => updateQueryParams({ place })}
            onRelocationToggle={isRelocation =>
              updateQueryParams({
                isRelocation: isRelocation ? 'true' : undefined,
              })
            }
            onRemoteToggle={isRemote =>
              updateQueryParams({
                isRemote: isRemote ? 'true' : undefined,
              })
            }
            onSelectGlobal={() => {
              setIsPlacesSearchOpen(false);
              updateQueryParams({
                isOnSite: undefined,
                isRelocation: undefined,
                isRemote: undefined,
                place: undefined,
              });
            }}
            onSiteToggle={isOnSite =>
              updateQueryParams({
                isOnSite: isOnSite ? 'true' : undefined,
              })
            }
            selectedPlace={place}
          />
        </div>
      )}

      {!isFiltersHidden && (
        <div
          className={classnames(
            'talent-pipeline-sidebar__search',
            isSearchOpen && 'talent-pipeline-sidebar__search--active',
          )}
        >
          <Search
            onDropdownToggle={setIsSearchOpen}
            onFilterClick={() => setIsFiltersOpen(!isFiltersOpen)}
            onQueryParamsSelect={queryParams => updateQueryParams(queryParams)}
            onSelect={searchPatterns => updateQueryParams({ searchPatterns })}
            queryParams={queryParams}
            values={filteredSearchPatterns}
          />
        </div>
      )}

      {!isSortByHidden && (
        <div className="talent-pipeline-sidebar__talents-header">
          <div className="talent-pipeline-sidebar__talents-header-title">Sort by</div>

          <div className="talent-pipeline-sidebar__talents-header-select">
            <SortingOptions
              onClear={() => onGroupedTalentsClick({ groupedTalentDictionaryKey: undefined })}
              onOptionChange={groupedTalentDictionaryKey => onGroupedTalentsClick({ groupedTalentDictionaryKey })}
              options={sortingOptions}
              selectedOption={groupedTalentsKey}
            />
          </div>
        </div>
      )}

      <div className="talent-pipeline-sidebar__talents-list" ref={talentsListRef}>
        <TalentList
          isLoading={isVerifiedTalentsListFeedInitialFetchLoading || isVerifiedTalentsListFeedFetchMoreLoading}
          list={verifiedTalentsListFeed}
          onTalentClick={(index, userId) => {
            if (isSubscriptionInactive) {
              return;
            }

            onGroupedTalentsClick({
              groupedTalentDictionaryKey: groupedTalentsKey,
              index: index,
              userId: userId,
            });
          }}
        />
      </div>

      <DrawerSideBar isOpen={isFiltersOpen} onOutsideClick={() => setIsFiltersOpen(false)} side={Side.LEFT}>
        <SidebarFilters
          onFilterSelect={onFilterSelect}
          selectedSalaryRange={parsedSalaryRange}
          selectedSeniorities={seniorities?.map(({ id }) => id)}
          selectedSpecialities={specialities.map(({ id }) => id)}
        />
      </DrawerSideBar>
    </div>
  );
};
